import {
    FETCH_MESSAGES,
    FETCH_MESSAGES_SUCCESS,
    FETCH_MESSAGES_FAILED,
    SEND_MESSAGE,
    STOP_FETCH_MESSAGES
} from "../store/types";
import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { RequestPushMsg } from '../other/NotificationFunctions';
import { onValue, push, off, child, runTransaction, update } from "firebase/database";

let activeChatUnsubscribe = null;
let activeChatBookingId = null;

export const parseChatSnapshot = (rootEntry) => {
    let allMesseges = [];
    if (!rootEntry || typeof rootEntry !== 'object') return allMesseges;
    for (let key in rootEntry) {
        let entryKey = rootEntry[key];
        if (entryKey && typeof entryKey === 'object') {
            if (entryKey.message !== undefined) {
                entryKey.smsId = key;
                allMesseges.push(entryKey);
            } else {
                for (let msgKey in entryKey) {
                    if (entryKey[msgKey] && typeof entryKey[msgKey] === 'object') {
                        entryKey[msgKey].smsId = msgKey;
                        allMesseges.push(entryKey[msgKey]);
                    }
                }
            }
        }
    }
    const parseTime = (m) => {
        if (typeof m.timestamp === 'number' && !isNaN(m.timestamp)) return m.timestamp;
        if (m.createdAt) {
            const t = new Date(m.createdAt).getTime();
            if (!isNaN(t)) return t;
        }
        return 0;
    };
    allMesseges.sort((a, b) => parseTime(a) - parseTime(b));
    return allMesseges;
};

const loadChatViaRest = async (cleanBookingId, dispatch) => {
    try {
        const { getValidAuthToken } = require("./authactions");
        let token = await getValidAuthToken();
        if (!token) return false;

        const baseUrl = (firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
        let rtdbUrl = `${baseUrl}/chats/${cleanBookingId}/messages.json?auth=${token}`;
        let res = await fetch(rtdbUrl);
        if (res.status === 401) {
            token = await getValidAuthToken(true);
            if (token) {
                rtdbUrl = `${baseUrl}/chats/${cleanBookingId}/messages.json?auth=${token}`;
                res = await fetch(rtdbUrl);
            }
        }
        if (res.ok) {
            const data = await res.json();
            const msgs = parseChatSnapshot(data);
            dispatch({
                type: FETCH_MESSAGES_SUCCESS,
                payload: msgs,
            });
            return true;
        }
    } catch (e) {
        console.warn('[CHAT] REST fetch error:', e?.message);
    }
    return false;
};

export const fetchChatMessages = (bookingId) => async (dispatch) => {
    const { chatRef } = firebase;
    const cleanBookingId = String(bookingId || '').replace(/_(outbound|return)$/, '');
    if (!cleanBookingId) return;

    dispatch({
        type: FETCH_MESSAGES,
        payload: cleanBookingId,
    });

    // Clean up any existing listener before attaching a new one
    if (activeChatUnsubscribe) {
        try { activeChatUnsubscribe(); } catch (_) {}
        activeChatUnsubscribe = null;
    }
    if (activeChatBookingId && chatRef) {
        try { off(chatRef(activeChatBookingId)); } catch (_) {}
    }

    // 1. Immediate REST fetch for instant display without waiting for WebSocket handshake
    loadChatViaRest(cleanBookingId, dispatch);

    // 2. Realtime onValue listener for live updates
    if (chatRef) {
        try {
            const unsub = onValue(chatRef(cleanBookingId), snapshot => {
                if (snapshot.val()) {
                    const allMesseges = parseChatSnapshot(snapshot.val());
                    dispatch({
                        type: FETCH_MESSAGES_SUCCESS,
                        payload: allMesseges,
                    });
                } else {
                    dispatch({
                        type: FETCH_MESSAGES_SUCCESS,
                        payload: [],
                    });
                }
            }, async (error) => {
                console.warn('[CHAT] onValue error, retrying via REST:', error?.message);
                const ok = await loadChatViaRest(cleanBookingId, dispatch);
                if (!ok) {
                    dispatch({
                        type: FETCH_MESSAGES_FAILED,
                        payload: error?.message || store.getState()?.languagedata?.defaultLanguage?.chat_not_found,
                    });
                }
            });
            activeChatUnsubscribe = unsub;
            activeChatBookingId = cleanBookingId;
        } catch (err) {
            console.error('[CHAT] onValue attach error:', err);
            loadChatViaRest(cleanBookingId, dispatch);
        }
    }
};

export const sendMessage = (data) => async (dispatch) => {
    const { chatRef, singleBookingRef } = firebase;

    const extractId = (val) => {
        if (!val) return '';
        if (typeof val === 'string' || typeof val === 'number') {
            const str = String(val).trim();
            return str === '[object Object]' ? '' : str;
        }
        if (typeof val === 'object') {
            return val.uid || val.id || val.key || val._id || val.driverId || val.customerId || '';
        }
        return '';
    };

    const booking = data?.booking || {};
    let customerId = extractId(booking.customer) || extractId(booking.customer_uid) || extractId(booking.customer_id) || extractId(booking.customerId);
    let driverId = extractId(booking.driver) || extractId(booking.driverUid) || extractId(booking.driver_uid) || extractId(booking.driver_id) || extractId(booking.driverId);

    const profile = store.getState()?.auth?.profile;
    if (!customerId && data?.role === 'customer' && profile?.uid) {
        customerId = profile.uid;
    }
    if (!driverId && data?.role === 'driver' && profile?.uid) {
        driverId = profile.uid;
    }

    const sanitizeKey = (k, fallback) => {
        const clean = String(k || '').replace(/[.#$[\]/]/g, '_').trim();
        return clean.length > 0 && clean !== '[object Object]' ? clean : fallback;
    };

    const cleanCustomerId = sanitizeKey(customerId, 'customer');
    const cleanDriverId = sanitizeKey(driverId, 'driver');
    const chatId = `${cleanCustomerId},${cleanDriverId}`;

    const now = new Date();
    const time = String(now.getHours()).padStart(2, '0') + ":" + String(now.getMinutes()).padStart(2, '0');
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0'); 
    const yyyy = now.getFullYear();
    const today = mm + ':' + dd + ':' + yyyy;
    
    const fromId = data?.role === 'customer' ? (profile?.uid || cleanCustomerId) : (profile?.uid || cleanDriverId);

    const msg = {
        message: data?.message,
        from: fromId,
        type: "msg",
        msgDate: today,
        msgTime: time,
        createdAt: now.toISOString(),
        timestamp: Date.now(),
        source: data?.role
    };

    const bookingId = booking.id || booking.bookingId || data?.bookingId;
    if (bookingId && chatRef) {
        const cleanBookingId = String(bookingId).replace(/_(outbound|return)$/, '');
        try {
            const pushRef = await push(child(chatRef(cleanBookingId), chatId), msg);
            if (pushRef && pushRef.key) {
                msg.smsId = pushRef.key;
            }
        } catch (err) {
            console.error('[CHAT] Firebase push error:', err);
        }

        // Increment recipient unread counter
        const targetField = data?.role === 'driver' ? 'unreadCustomer' : 'unreadDriver';
        if (singleBookingRef) {
            try {
                const counterRef = child(singleBookingRef(cleanBookingId), targetField);
                if (typeof runTransaction === 'function') {
                    await runTransaction(counterRef, (current) => (current || 0) + 1);
                } else if (typeof counterRef.transaction === 'function') {
                    await counterRef.transaction((current) => (current || 0) + 1);
                }
            } catch (err) {
                console.warn('[CHAT] Failed to increment unread counter:', err?.message || err);
            }
        }

        const defaultLang = store.getState()?.languagedata?.defaultLanguage;
        const notifTitle = (defaultLang?.notification_title || 'Driver 2GO: ') + (defaultLang?.chat_requested || 'Nouveau message');
        const driverToken = booking.driver_token || (typeof booking.driver === 'object' ? booking.driver?.pushToken : null);
        const customerToken = booking.customer_token || (typeof booking.customer === 'object' ? booking.customer?.pushToken : null);

        if (data?.role === 'customer') {
            if (driverToken) {
                RequestPushMsg(
                    driverToken,
                    {
                        title: notifTitle,
                        msg: data.message,
                        screen: 'onlineChat',
                        params: { bookingId: cleanBookingId }
                    });
            }
        } else {
            if (customerToken) {
                RequestPushMsg(
                    customerToken,
                    {
                        title: notifTitle,
                        msg: data.message,
                        screen: 'onlineChat',
                        params: { bookingId: cleanBookingId }
                    });
            }
        }
    }

    dispatch({
        type: SEND_MESSAGE,
        payload: msg,
    });

    return msg;
};

export const stopFetchMessages = (bookingId) => (dispatch) => {
    const { chatRef } = firebase;
    const cleanBookingId = String(bookingId || '').replace(/_(outbound|return)$/, '');

    if (activeChatUnsubscribe) {
        try { activeChatUnsubscribe(); } catch (_) {}
        activeChatUnsubscribe = null;
    }
    if (cleanBookingId && chatRef) {
        try { off(chatRef(cleanBookingId)); } catch (_) {}
    }
    activeChatBookingId = null;

    dispatch({
        type: STOP_FETCH_MESSAGES,
        payload: cleanBookingId,
    });
};

export const clearUnreadMessages = (bookingId, role) => async (dispatch) => {
    const { singleBookingRef } = firebase;
    const cleanBookingId = String(bookingId || '').replace(/_(outbound|return)$/, '');
    if (!cleanBookingId || !singleBookingRef) return;

    const userRole = role || store.getState()?.auth?.profile?.usertype;
    if (!userRole) return;

    const targetField = userRole === 'driver' ? 'unreadDriver' : 'unreadCustomer';
    const counterRef = child(singleBookingRef(cleanBookingId), targetField);

    try {
        if (typeof runTransaction === 'function') {
            await runTransaction(counterRef, () => 0);
        } else if (typeof counterRef.transaction === 'function') {
            await counterRef.transaction(() => 0);
        } else if (typeof update === 'function') {
            await update(singleBookingRef(cleanBookingId), { [targetField]: 0 });
        }
    } catch (err) {
        console.warn('[CHAT] Failed to clear unread counter:', err?.message || err);
    }
};